// useLoginConfig.ts — asks the app's own backend what sign-in it supports.
//
// Answers one question: can this deployment take a password directly, or does
// the user have to be sent to the identity provider? The backend
// (lib/oidcrp's LoginConfig) works that out from its own configuration and
// from what the issuer says about this client, so the SPA doesn't have to
// know — or be rebuilt when the answer changes.
//
// Failure is not an error state. A backend that predates the config route
// 404s, and an unreachable one throws; both mean "no embedded form here",
// which is the behavior every app had before embedded login existed.

import { useEffect, useState } from 'react';

export interface LoginConfig {
    /** The backend accepts credentials at its password-login route. */
    password: boolean;
    /** Social providers worth offering as one-click buttons (`idp_hint`s). */
    providers: string[];
    /** Identity provider behind the redirect flow, for labelling. */
    issuer: string;
    /** True until the probe settles. Render nothing decisive before then. */
    loading: boolean;
}

const FALLBACK: Omit<LoginConfig, 'loading'> = {
    password: false,
    providers: ['google', 'github'],
    issuer: 'authz.lab0.us',
};

export function useLoginConfig(configPath = '/api/oidc/config'): LoginConfig {
    const [config, setConfig] = useState<LoginConfig>({ ...FALLBACK, loading: true });

    useEffect(() => {
        let cancelled = false;

        (async () => {
            let next = { ...FALLBACK };
            try {
                const res = await fetch(configPath, { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    next = {
                        password: Boolean(data.password),
                        providers: Array.isArray(data.providers) ? data.providers : FALLBACK.providers,
                        issuer: typeof data.issuer === 'string' && data.issuer ? data.issuer : FALLBACK.issuer,
                    };
                } else {
                    console.info(`[oidc] ${configPath} returned ${res.status}; using the redirect flow`);
                }
            } catch (err) {
                console.info(`[oidc] ${configPath} unreachable (${err}); using the redirect flow`);
            }
            if (!cancelled) setConfig({ ...next, loading: false });
        })();

        return () => { cancelled = true; };
    }, [configPath]);

    return config;
}
